import type { IExtendableDto } from "../../dto";
import type {
  IAppliedRideStatusUpdates,
  IRideStatusUpdate,
} from "../../models";

/**
 * Applies a live status push to the loaded ride rows. The push's `updatedAt` is copied too, when the server sent it, so
 * the Kanban's "Updated" order follows a live status change without a refetch.
 */
export const applyRideStatusUpdates = <T extends IExtendableDto>({
  rows,
  updates,
}: {
  rows: T[];
  updates: IRideStatusUpdate[];
}): IAppliedRideStatusUpdates<T> => {
  const updatesById = new Map(updates.map(update => [update.id, update]));

  const updatedRows = rows.map(row => {
    const update = updatesById.get(row.id);

    if (!update) {
      return row;
    }

    const updatedRow: T = update.updatedAt
      ? { ...row, status: update.status, updatedAt: update.updatedAt }
      : { ...row, status: update.status };

    return updatedRow;
  });

  const result: IAppliedRideStatusUpdates<T> = {
    updatedRows,
    hasMatchedRows: rows.some(row => updatesById.has(row.id)),
  };

  return result;
};
